import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const LISTS = [
  { key: "research_areas", label: "Research areas", fields: [["title", "Title", false], ["body", "Body", true]], empty: { title: "", body: "" } },
  { key: "faqs", label: "FAQs", fields: [["q", "Question", false], ["a", "Answer", true]], empty: { q: "", a: "" } },
  { key: "glossary", label: "Glossary", fields: [["term", "Term", false], ["definition", "Definition", false]], empty: { term: "", definition: "" } },
  { key: "references", label: "References", fields: [["n", "#", false], ["title", "Title", false], ["url", "URL", false], ["source", "Source", false]], empty: { n: "", title: "", url: "", source: "" } },
];

function ListEditor({ def, rows, onChange }) {
  const update = (i, k, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <div data-testid={`guide-edit-list-${def.key}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F]">{def.label} ({rows.length})</p>
        <button type="button" onClick={() => onChange([...rows, { ...def.empty }])} className="text-xs text-[#8C7B6B] font-semibold inline-flex items-center gap-1 hover:text-[#1A1A1A]" data-testid={`guide-edit-add-${def.key}`}>
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={`${def.key}-${i}`} className="border border-[#DDD7CF] rounded-md p-3 bg-[#F7F5F2] relative">
            <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} className="absolute top-2 right-2 text-[#B42318] hover:bg-[#FBEAEA] rounded p-1" aria-label="Remove row">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <div className={`grid gap-2 ${def.fields.length > 2 ? "grid-cols-[3rem_1fr_1fr_1fr]" : "grid-cols-1"} pr-7`}>
              {def.fields.map(([k, label, multi]) => multi ? (
                <Textarea key={k} rows={3} value={r[k] ?? ""} onChange={(e) => update(i, k, e.target.value)} placeholder={label} className="text-sm bg-white" />
              ) : (
                <Input key={k} value={r[k] ?? ""} onChange={(e) => update(i, k, e.target.value)} placeholder={label} className="text-sm bg-white" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Flags = ({ guide }) => {
  const total = (guide.dosing_flags?.length || 0) + (guide.source_flags?.length || 0);
  if (!total) return null;
  return (
    <div className="bg-[#FEF3E2] border border-[#B45309]/40 rounded-md p-3 text-xs text-[#7C3A0B]" data-testid="guide-edit-flags">
      <p className="font-semibold flex items-center gap-1.5 mb-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Held for review — fix these before publishing</p>
      <ul className="space-y-1 list-disc pl-4">
        {(guide.dosing_flags || []).map((f, i) => <li key={`d-${i}`}><span className="font-mono">{f.field}</span>: “…{f.context}…” <strong>({f.match})</strong></li>)}
        {(guide.source_flags || []).map((s, i) => <li key={`s-${i}`}>Unrecognised source: {s}</li>)}
      </ul>
    </div>
  );
};

export function GuideEditDialog({ guide, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (guide) {
      setForm({
        quick_answer: guide.quick_answer, what_it_is: guide.what_it_is, mechanism: guide.mechanism, evidence: guide.evidence,
        research_areas: guide.research_areas || [], faqs: guide.faqs || [], glossary: guide.glossary || [],
        references: (guide.references || []).map((r) => ({ ...r, n: String(r.n) })),
      });
    }
  }, [guide]);

  if (!guide || !form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, references: form.references.map((r) => ({ ...r, n: parseInt(r.n, 10) || 0 })) };
      const { data } = await api.put(`/admin/guides/${guide.id}`, payload);
      const flags = (data.dosing_flags?.length || 0) + (data.source_flags?.length || 0);
      const demoted = guide.status === "published" && data.status === "draft";
      toast[flags ? "warning" : "success"](flags
        ? `Saved — ${flags} flag(s) still need attention${demoted ? "; guide moved to draft until you republish" : ""}`
        : "Guide saved — no flags remaining");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save guide");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="guide-edit-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-sm uppercase tracking-wide">Edit guide — {guide.peptide}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <Flags guide={guide} />
          {[["quick_answer", "Quick answer", 3], ["what_it_is", "What it is", 6], ["mechanism", "How it is thought to work", 6], ["evidence", "Where the evidence stands", 6]].map(([k, label, rows]) => (
            <div key={k}>
              <label className="text-xs text-[#6E675F] block mb-1.5">{label}</label>
              <Textarea data-testid={`guide-edit-${k}`} rows={rows} value={form[k]} onChange={set(k)} className="text-sm" />
            </div>
          ))}
          {LISTS.map((def) => (
            <ListEditor key={def.key} def={def} rows={form[def.key]} onChange={(rows) => setForm((f) => ({ ...f, [def.key]: rows }))} />
          ))}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="guide-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]" data-testid="guide-edit-save">{saving ? "Saving…" : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
